import { createFileRoute, Link } from "@tanstack/react-router";
import { canonical } from "@/lib/seo";

export const Route = createFileRoute("/explore")({
  head: () => ({
    meta: [
      { title: "Explore Crystal Beach - Malak BnB Resort" },
      { name: "description", content: "Discover the charm of Crystal Beach, Lake Erie, and Niagara Falls from Malak BnB Resort." },
      { property: "og:title", content: "Explore Crystal Beach - Malak BnB Resort" },
      { property: "og:description", content: "Discover the charm of Crystal Beach, Lake Erie, and Niagara Falls." },
      { property: "og:url", content: "https://malakbnbresort.ca/explore" },
      { property: "og:image", content: "/images/explore-crystal-beach.webp" },
    ],
    links: [canonical("/explore")],
  }),
  component: ExplorePage,
});

const IMAGES = {
  hero: "/images/explore-crystal-beach.webp",
  beach: "/images/beach-bay.webp",
  biking: "/images/explore-biking.webp",
  ridge: "/images/explore-ridge-beach.webp",
  fishing: "/images/explore-fishing.webp",
};

const attractions = [
  { img: IMAGES.beach, title: "Bay Beach", desc: "Golden sands and calm waters just a short walk from the resort." },
  { img: IMAGES.biking, title: "Cycling & Town", desc: "Explore the charming streets and local shops of Crystal Beach by bike." },
  { img: IMAGES.ridge, title: "Ridge Beach", desc: "Scenic shorelines with breathtaking views of Lake Erie." },
  { img: IMAGES.fishing, title: "Marina & Fishing", desc: "Cast a line at the local marina or take a boat out on the lake." },
];

function ExplorePage() {
  return (
    <div>
      {/* Hero */}
      <section className="relative h-[50vh] min-h-[350px] overflow-hidden">
        <img src={IMAGES.hero} alt="Crystal Beach town" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/60 to-transparent" />
        <div className="relative z-10 flex h-full items-end pb-14 px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl w-full">
            <h1 className="text-4xl sm:text-5xl font-heading text-white animate-fade-in-up">Explore Crystal Beach</h1>
          </div>
        </div>
      </section>

      {/* Resort Description */}
      <section className="py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-heading text-foreground">An Urban Luxury Resort</h2>
          <p className="mt-6 text-muted-foreground leading-relaxed">
            From the moment you arrive at the Malak BnB Resort, you can expect to feel warm hospitality
            as you would in the comfort of your own home. Situated close to Bay Beach and Niagara Falls,
            the entire resort is designed to capture the spirit of relaxation and the calm atmosphere of
            Crystal Beach.
          </p>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            The resort features an outdoor heated swimming pool, continental breakfast buffet and lounge
            areas indoor and outdoor. We assure your stay will be a breath of fresh air!
          </p>
        </div>
      </section>

      {/* Local Attractions */}
      <section className="py-20 bg-card">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">Local Attractions</p>
            <h2 className="text-3xl font-heading text-foreground">Discover What's Nearby</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {attractions.map((a) => (
              <div key={a.title} className="rounded-2xl overflow-hidden bg-background shadow-sm hover:shadow-xl transition-shadow duration-300">
                <div className="aspect-[3/4] overflow-hidden">
                  <img src={a.img} alt={a.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" loading="lazy" />
                </div>
                <div className="p-5">
                  <h3 className="font-heading text-lg text-foreground">{a.title}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{a.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/blog/things-to-do-near-crystal-beach-ontario" className="inline-flex items-center text-primary font-semibold text-sm hover:underline">
              Read the full guide to things to do near Crystal Beach →
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="mx-auto max-w-3xl px-4 text-center">
          <h2 className="text-3xl font-heading text-foreground">Ready to Explore?</h2>
          <p className="mt-4 text-muted-foreground">Book your stay and discover the charm of Crystal Beach.</p>
          <Link
            to="/book"
            className="mt-8 inline-flex items-center justify-center rounded-full bg-primary px-10 py-4 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors shadow-lg"
          >
            Request to Book
          </Link>
        </div>
      </section>
    </div>
  );
}
